import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Branch, type LegalEntity } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import { changed, FormModal, FormSection } from '../components/FormModal.tsx';
import { CepField } from '../components/forms/CepField.tsx';
import {
  ListToolbar,
  matchesStatus,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../components/ListFilters.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Select,
  Table,
} from '../components/ui.tsx';
import { MasterDeleteButton } from '../components/MasterDelete.tsx';
import { CountrySelect } from './partners/common.tsx';

export const BRANCH_KINDS = [
  'headquarters',
  'branch',
  'warehouse',
  'hub',
  'factory',
  'office',
  'store',
  'other',
] as const;
const ADDRESS = ['line1', 'line2', 'city', 'region', 'postalCode'] as const;

export type BranchForm = {
  legalEntityId: string;
  code: string;
  name: string;
  kind: string;
  country: string;
  timezone: string;
  taxId: string;
  stateRegistration: string;
  municipalRegistration: string;
  address: Record<(typeof ADDRESS)[number], string>;
  isActive: boolean;
};
type Form = BranchForm;

export function emptyBranchForm(legalEntity?: LegalEntity): Form {
  return {
    legalEntityId: legalEntity?.id ?? '',
    code: '',
    name: '',
    kind: 'branch',
    country: legalEntity?.country ?? 'BR',
    timezone: legalEntity?.timezone ?? 'America/Sao_Paulo',
    taxId: '',
    stateRegistration: '',
    municipalRegistration: '',
    address: { line1: '', line2: '', city: '', region: '', postalCode: '' },
    isActive: true,
  };
}

export function branchToForm(b: Branch): Form {
  return {
    ...b,
    taxId: b.taxId ?? '',
    stateRegistration: b.stateRegistration ?? '',
    municipalRegistration: b.municipalRegistration ?? '',
    address: Object.fromEntries(ADDRESS.map((k) => [k, b.address[k] ?? ''])) as Form['address'],
  };
}

export function branchPayload(f: Form) {
  return { ...f, code: f.code.toUpperCase(), country: f.country.toUpperCase() };
}

type Editing = { id: string | null; form: Form; initial: Form };

export function BranchesPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  // A permissão pode valer só para algumas empresas; o servidor confere o escopo de cada uma.
  const canManage = can(me, 'core.branch.manage');
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');

  const entities = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  const list = useQuery({
    queryKey: ['branches', filter],
    queryFn: () =>
      api.get<Branch[]>(
        `/core/branches${filter ? `?legalEntityId=${encodeURIComponent(filter)}` : ''}`,
      ),
  });

  const save = useMutation({
    mutationFn: (e: Editing) =>
      e.id
        ? api.put<Branch>(`/core/branches/${e.id}`, branchPayload(e.form))
        : api.post<Branch>('/core/branches', branchPayload(e.form)),
    onSuccess: async () => {
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['branches'] });
    },
  });

  const open = (id: string | null, form: Form) => {
    save.reset();
    setEditing({ id, form, initial: form });
  };
  const update = (patch: Partial<Form>) =>
    setEditing((cur) => cur && { ...cur, form: { ...cur.form, ...patch } });
  const entityById = new Map(entities.data?.map((e) => [e.id, e]));
  const needle = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (b) =>
      matchesStatus(b.isActive, status) &&
      (!needle ||
        [b.code, b.name, b.taxId, b.address.city].some((v) => v?.toLowerCase().includes(needle))),
  );

  return (
    <>
      <PageHeader
        title={t('branches.title')}
        description={t('branches.description')}
        actions={
          canManage && !editing ? (
            <Button
              onClick={() =>
                open(null, emptyBranchForm(entityById.get(filter) ?? entities.data?.[0]))
              }
              disabled={!entities.data?.length}
            >
              {t('branches.new')}
            </Button>
          ) : null
        }
      />

      <FormModal
        open={!!editing}
        title={editing?.id ? t('branches.edit') : t('branches.new')}
        saveLabel={editing?.id ? undefined : t('branches.create')}
        size="xl"
        dirty={!!editing && changed(editing.initial, editing.form)}
        saving={save.isPending}
        error={save.error}
        onSave={() => editing && save.mutate(editing)}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <FormSection columns={3}>
            <BranchFields
              form={editing.form}
              update={update}
              entities={entities.data ?? []}
              lockedEntity={!!editing.id}
              error={save.error}
            />
          </FormSection>
        )}
      </FormModal>

      <ListToolbar>
        <SearchBox value={q} onChange={setQ} placeholder={t('legalEntities.record.searchBranch')} />
        <StatusFilter value={status} onChange={setStatus} />
        <Select
          id="br-filter"
          className="w-full sm:w-64"
          aria-label={t('branches.filterByEntity')}
          value={filter}
          onChange={(ev) => setFilter(ev.target.value)}
        >
          <option value="">{t('common.allCompanies')}</option>
          {entities.data?.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.legalName}
            </option>
          ))}
        </Select>
      </ListToolbar>

      {list.isPending ? (
        <Loading />
      ) : list.isError ? (
        <ErrorBox error={list.error} />
      ) : (
        <Table
          head={[
            t('fields.code'),
            t('fields.name'),
            t('branches.legalEntity'),
            t('fields.country'),
            t('fields.timezone'),
            t('fields.status'),
            '',
          ]}
        >
          {rows.length === 0 && <EmptyRow colSpan={7}>{t('branches.empty')}</EmptyRow>}
          {rows.map((b) => (
            <tr key={b.id}>
              <td className="font-mono">{b.code}</td>
              <td>
                {b.name}
                <div className="text-xs text-muted">{t(`branches.kinds.${b.kind}`)}</div>
              </td>
              <td className="font-mono text-xs">{entityById.get(b.legalEntityId)?.code ?? '—'}</td>
              <td>{b.country}</td>
              <td className="text-xs">{b.timezone}</td>
              <td>
                {b.isActive ? (
                  <Badge tone="ok">{t('fields.active')}</Badge>
                ) : (
                  <Badge tone="off">{t('fields.inactive')}</Badge>
                )}
              </td>
              <td className="text-right">
                <span className="flex justify-end gap-2">
                  {canManage && (
                    <Button variant="secondary" onClick={() => open(b.id, branchToForm(b))}>
                      {t('common.edit')}
                    </Button>
                  )}
                  <MasterDeleteButton table="core.branch" id={b.id} compact />
                </span>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

/**
 * Campos da filial, usados na tela Filiais e na ficha da Empresa (aba Filiais), para as duas terem
 * exatamente as mesmas regras. Quem chama monta a grade e os botões.
 */
export function BranchFields({
  form,
  update,
  entities,
  lockedEntity,
  lockedHint,
  error,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  entities: LegalEntity[];
  /** A empresa não muda depois de criada (ou vem da ficha). */
  lockedEntity: boolean;
  /** Dica no lugar de "não dá para mudar" (ex.: a empresa vem da ficha). */
  lockedHint?: string;
  error: unknown;
}) {
  const { t } = useTranslation();
  const text =
    (
      k:
        | 'code'
        | 'name'
        | 'kind'
        | 'timezone'
        | 'taxId'
        | 'stateRegistration'
        | 'municipalRegistration',
    ) =>
    (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      update({ [k]: ev.target.value });
  return (
    <>
      <Field
        label={t('branches.legalEntity')}
        hint={lockedEntity ? (lockedHint ?? t('branches.legalEntityLocked')) : undefined}
        required
      >
        <Select
          id="br-entity"
          required
          disabled={lockedEntity}
          value={form.legalEntityId}
          onChange={(ev) => update({ legalEntityId: ev.target.value })}
        >
          {entities.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.legalName}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t('fields.code')}
        hint={t('fields.codeHint')}
        error={issueFor(error, 'code')}
        required
      >
        <Input id="br-code" required value={form.code} onChange={text('code')} />
      </Field>
      <Field label={t('fields.name')} error={issueFor(error, 'name')} required>
        <Input id="br-name" required value={form.name} onChange={text('name')} />
      </Field>
      <Field label={t('branches.kind')} required>
        <Select id="br-kind" value={form.kind} onChange={text('kind')}>
          {BRANCH_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`branches.kinds.${k}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t('fields.country')} error={issueFor(error, 'country')} required>
        <CountrySelect
          id="br-country"
          value={form.country}
          placeholder={t('common.chooseCountry')}
          onChange={(country) => update({ country })}
        />
      </Field>
      <Field
        label={t('fields.timezone')}
        hint={t('fields.timezoneHint')}
        error={issueFor(error, 'timezone')}
        required
      >
        <Input id="br-timezone" required value={form.timezone} onChange={text('timezone')} />
      </Field>
      <Field label={t('branches.taxId')} error={issueFor(error, 'taxId')}>
        <Input id="br-tax-id" value={form.taxId} onChange={text('taxId')} />
      </Field>
      <Field label={t('branches.stateRegistration')}>
        <Input id="br-ie" value={form.stateRegistration} onChange={text('stateRegistration')} />
      </Field>
      <Field label={t('branches.municipalRegistration')}>
        <Input
          id="br-im"
          value={form.municipalRegistration}
          onChange={text('municipalRegistration')}
        />
      </Field>
      {form.country === 'BR' && (
        <CepField
          id="br-address-postalCode"
          value={form.address.postalCode}
          onChange={(postalCode) => update({ address: { ...form.address, postalCode } })}
          onFound={(found) =>
            update({
              address: {
                ...form.address,
                postalCode: found.postalCode ?? form.address.postalCode,
                line1: found.street ?? form.address.line1,
                city: found.city ?? form.address.city,
                region: found.region ?? form.address.region,
              },
            })
          }
        />
      )}
      {ADDRESS.filter((k) => k !== 'postalCode' || form.country !== 'BR').map((k) => (
        <Field key={k} label={t(`branches.address.${k}`)}>
          <Input
            id={`br-address-${k}`}
            value={form.address[k]}
            onChange={(ev) => update({ address: { ...form.address, [k]: ev.target.value } })}
          />
        </Field>
      ))}
      <label className="flex items-center gap-2 text-sm">
        <input
          id="br-active"
          type="checkbox"
          checked={form.isActive}
          onChange={(ev) => update({ isActive: ev.target.checked })}
        />
        {t('fields.active')}
      </label>
    </>
  );
}
